import axios from 'axios';
import remove from 'lodash/remove';
import {
    API_URL_WINTER,
    API_URL_TRANSPORTATION,
    API_URL_TEXTS,
    API_URL_POIS,
} from '~/config/settings/settingsApi';
import {
    WEBCAM_MAENNLICHEN,
    WEBCAM_EIGERGLETSCHER,
    WEBCAM_KLEINESCHEIDEGG,
    WEBCAM_SUMMER_MAENNLICHEN,
    WEBCAM_SUMMER_EIGERGLETSCHER,
    WEBCAM_SUMMER_KLEINESCHEIDEGG,
    WEBCAM_FIRST,
    WEBCAM_BUSSALP,
    WEBCAM_FAULHORN,
    WEBCAM_BIRG,
    WEBCAM_ALLMENDHUBEL,
    WEBCAM_SCHRECKFELD,
} from '~/config/settings/settingsWebcamPreviews';

import { IS_PRE_SEASON, PRE_SEASON_LIFTS } from '~/config/settings/settingsPreSeason';
import { LIFTS_HIKING, LIFTS_TOBOGGANING, POIS_HIKING, POIS_TOBOGGANING, TRACKS_TOBOGGANING, TRACKS_HIKING, LIFTS_NOT_SKIING, POIS_NOT_SKIING } from '~/config/settings/settingsActivityModes';
import * as idsTextApiLocations from '~/config/ids/idsTextApiLocations';
import * as idsSismedia from '~/config/ids/idsSismedia';
import * as resorts from '~/config/ids/idsResorts';

export default {

    error: false,
    data: {},
    dataWinterAPI: null,
    dataTransportAPI: null,
    dataTextAPI: null,
    dataPoisAPI: null,

    /**
     *
     */
    async loadResortData() {
        this.data = {};

        await Promise.all([
            this.fetchAPIWinter(),
            this.fetchAPITransportation(),
            this.fetchAPITexts(),
            this.fetchPOIData(),
        ]);

        if (this.dataWinterAPI) {
            this.setData();
            this.removeUnusedData();
            this.addTransportAPILifts();
            this.addCustomLifts();
            this.addCustomTracks();
            this.modifyTracks();
            this.applyPreSeasonSettings();
        }

        this.applyActivityModeSettings();

        return {
            data: this.data,
            error: this.error,
        };
    },

    /**
     *
     */
    async fetchAPIWinter() {
        try {
            const apiData = await axios.get(API_URL_WINTER);
            this.dataWinterAPI = apiData.data.data;
        } catch (error) {
            console.error('Error fetching winter API data', error);
            this.dataWinterAPI = null;
            this.error = error.message;
        }
    },

    /**
     *
     */
    async fetchAPITransportation() {
        try {
            const transportApiData = await axios.get(API_URL_TRANSPORTATION);
            this.dataTransportAPI = transportApiData.data.data;
        } catch (error) {
            console.error('Error fetching transport API data', error);
            this.dataTransportAPI = null;
            this.error = error.message;
        }
    },

    /**
     *
     */
    async fetchAPITexts() {
        try {
            const textsApiData = await axios.get(API_URL_TEXTS);
            this.dataTextAPI = textsApiData.data.data;
        } catch (error) {
            console.error('Error fetching text API data', error);
            this.dataTextAPI = null;
            this.error = error.message;
        }
    },

    /**
     *
     */
    async fetchPOIData() {
        try {
            const pois = await axios.get(API_URL_POIS);
            this.dataPoisAPI = pois.data;
        } catch (error) {
            console.error('Error fetching POI data', error);
            this.dataPoisAPI = null;
            this.error = error.message;
        }
    },

    /**
     *
     */
    setData() {

        const dataGW = this.dataWinterAPI.resorts.find(item => item.resort.sismediaId === idsSismedia.SISMEDIA_ID_GW);
        const dataGF = this.dataWinterAPI.resorts.find(item => item.resort.sismediaId === idsSismedia.SISMEDIA_ID_GF);
        const dataMS = this.dataWinterAPI.resorts.find(item => item.resort.sismediaId === idsSismedia.SISMEDIA_ID_MS);

        this.data.tracks = [...dataGW.tracks, ...dataMS.tracks, ...dataGF.tracks];
        this.data.lifts = [...dataGW.lifts, ...dataMS.lifts, ...dataGF.lifts];
        this.data.pois = this.dataPoisAPI || [];
        this.data.resorts = {};

        this.data.resorts[resorts.WINTER_GW] = {
            resort: {
                ...dataGW.resort,
                id: resorts.WINTER_GW,
                peak: {
                    elevation: 2320,
                }
            },
            summary: dataGW.summary,
            webcams: [
                WEBCAM_MAENNLICHEN,
                WEBCAM_EIGERGLETSCHER,
                WEBCAM_KLEINESCHEIDEGG,
            ],
            messages: this.dataTextAPI.locations ? this.dataTextAPI.locations.find(item => item.locationId === idsTextApiLocations.GW).activeTexts : [],
        };

        this.data.resorts[resorts.WINTER_GF] = {
            resort: {
                ...dataGF.resort,
                id: resorts.WINTER_GF,
            },
            summary: dataGF.summary,
            webcams: [
                WEBCAM_FIRST,
                WEBCAM_BUSSALP,
                WEBCAM_FAULHORN,
                WEBCAM_SCHRECKFELD,
            ],
            messages: this.dataTextAPI.locations ? this.dataTextAPI.locations.find(item => item.locationId === idsTextApiLocations.GF).activeTexts : [],
        };

        this.data.resorts[resorts.WINTER_MS] = {
            resort: {
                ...dataMS.resort,
                id: resorts.WINTER_MS,
            },
            summary: dataMS.summary,
            webcams: [
                WEBCAM_BIRG,
                WEBCAM_ALLMENDHUBEL,
            ],
            messages: this.dataTextAPI.locations ? this.dataTextAPI.locations.find(item => item.locationId === idsTextApiLocations.MS).activeTexts : [],
        };

        this.data.resorts[resorts.SUMMER_JR] = {
            resort: {
                id: resorts.SUMMER_JR,
                name: 'Jungfrau Region',
                sismediaId: idsSismedia.SISMEDIA_ID_JR,
                valley: {
                    elevation: 568,
                },
                peak: {
                    elevation: 3454,
                },
            },
            summary: {},
            webcams: [],
            messages: this.dataTextAPI.locations ? this.dataTextAPI.locations.find(item => item.locationId === idsTextApiLocations.GW).activeTexts : [],
        };

        this.data.resorts[resorts.SUMMER_KS] = {
            resort: {
                ...dataGW.resort,
                id: resorts.SUMMER_KS,
                name: 'Kleine Scheidegg',
                valley: {
                    elevation: 1034,
                },
                peak: {
                    elevation: 3454,
                },

            },
            summary: {},
            webcams: [
                WEBCAM_SUMMER_MAENNLICHEN,
                WEBCAM_SUMMER_EIGERGLETSCHER,
                WEBCAM_SUMMER_KLEINESCHEIDEGG,
            ],
            messages: this.dataTextAPI.locations ? this.dataTextAPI.locations.find(item => item.locationId === idsTextApiLocations.GW).activeTexts : [],
        };

        this.data.resorts[resorts.SUMMER_GF] = {
            resort: {
                ...dataGF.resort,
                id: resorts.SUMMER_GF,
            },
            summary: {},
            webcams: [
                WEBCAM_FIRST,
                WEBCAM_SCHRECKFELD,
            ],
            messages: this.dataTextAPI.locations ? this.dataTextAPI.locations.find(item => item.locationId === idsTextApiLocations.GF).activeTexts : [],
        };

        this.data.resorts[resorts.TERMINAL_WINTER_GL] = this.data.resorts[resorts.WINTER_GW];
        this.data.resorts[resorts.TERMINAL_WINTER_FW] = this.data.resorts[resorts.WINTER_GW];
    },

    /**
     *
     */
    removeUnusedData() {
        remove(this.data.tracks, item => item.trackType === 'REPLACEMENT');
    },

    /**
     * Add Railway and Lift Segments from Transport API
     */
    addTransportAPILifts() {
        if (this.dataTransportAPI) {
            const item1004 = this.dataTransportAPI.tracks
                .find(item => item.id === 'interlaken_ost_grindelwald')?.segments
                .find(segment => segment.id === 'segment_zweiluetschinen_grindelwald');

            this.data.lifts.push({
                id: 'segment_zweiluetschinen_grindelwald',
                name: 'Zweilütschinen - Grindelwald',
                liftType: 'RACK_RAILWAY',
                status: item1004?.status,
                sismediaResortId: idsSismedia.SISMEDIA_ID_GW,
                sismediaLiftId: idsSismedia.SISMEDIA_LIFT_ID_SEGMENT_ZWEILUETSCHINEN_GRINDELWALD,
            });

            const item1501 = this.dataTransportAPI.tracks
                .find(item => item.id === 'interlaken_ost_grindelwald')?.segments
                .find(segment => segment.id === 'segment_interlaken_ost_zweiluetschinen');

            this.data.lifts.push({
                id: 'segment_interlaken_ost_zweiluetschinen',
                name: 'Interlaken Ost - Zweilütschinen',
                liftType: 'RACK_RAILWAY',
                status: item1501?.status,
                sismediaResortId: idsSismedia.SISMEDIA_ID_GW,
                sismediaLiftId: idsSismedia.SISMEDIA_LIFT_ID_SEGMENT_INTERLAKEN_OST_ZWEILUETSCHINEN,
            });

            const item1502 = this.dataTransportAPI.tracks
                .find(item => item.id === 'interlaken_ost_grindelwald')?.segments
                .find(segment => segment.id === 'segment_zweiluetschinen_grindelwald');

            this.data.lifts.push({
                id: 'segment_zweiluetschinen_grindelwald',
                name: 'Zweilütschinen - Grindelwald',
                liftType: 'RACK_RAILWAY',
                status: item1502?.status,
                sismediaResortId: idsSismedia.SISMEDIA_ID_GW,
                sismediaLiftId: idsSismedia.SISMEDIA_LIFT_ID_SEGMENT_ZWEILUETSCHINEN_GRINDELWALD_2,
            });

            const item1503 = this.dataTransportAPI.tracks
                .find(item => item.id === 'interlaken_ost_lauterbrunnen')?.segments
                .find(segment => segment.id === 'segment_zweiluetschinen_lauterbrunnen');

            this.data.lifts.push({
                id: 'segment_zweiluetschinen_lauterbrunnen',
                name: 'Zweilütschinen - Lauterbrunnen',
                liftType: 'RACK_RAILWAY',
                status: item1503?.status,
                sismediaResortId: idsSismedia.SISMEDIA_ID_GW,
                sismediaLiftId: idsSismedia.SISMEDIA_LIFT_ID_SEGMENT_ZWEILUETSCHINEN_LAUTERBRUNNEN,
            });

            const item1504 = this.dataTransportAPI.tracks
                .find(item => item.id === 'wilderswil_schynige_platte')?.segments
                .find(segment => segment.id === 'segment_wilderswil_schynige_platte');

            this.data.lifts.push({
                id: 'segment_wilderswil_schynige_platte',
                name: 'Wilderswil - Schynige Platte',
                liftType: 'RACK_RAILWAY',
                status: item1504?.status,
                sismediaResortId: idsSismedia.SISMEDIA_ID_GW,
                sismediaLiftId: idsSismedia.SISMEDIA_LIFT_ID_SEGMENT_WILDERSWIL_SCHYNIGE_PLATTE,
            });

            const item1505 = this.dataTransportAPI.tracks
                .find(item => item.id === 'interlaken_harder_kulm')?.segments
                .find(segment => segment.id === 'segment_interlaken_harder_kulm');

            this.data.lifts.push({
                id: 'segment_interlaken_harder_kulm',
                name: 'Interlaken - Harder Kulm',
                liftType: 'FUNICULAR',
                status: item1505?.status,
                sismediaResortId: idsSismedia.SISMEDIA_ID_GW,
                sismediaLiftId: idsSismedia.SISMEDIA_LIFT_ID_SEGMENT_INTERLAKEN_HARDER_KULM,
            });
        }
    },

    /**
     *
     */
    addCustomLifts() {

    },

    /**
     *
     */
    addCustomTracks() {
        const eigergletscherTrack = this.data.tracks.find(item => item.sismediaTrackId === '118');
        this.data.tracks.push({
            id: 'eigergletscher_kleine_scheidegg',
            name: '31 Schwarzen',
            difficulty: 'EASY',
            trackType: 'PISTE_EASY',
            lengthInMeter: eigergletscherTrack ? eigergletscherTrack.lengthInMeter : 0,
            status: eigergletscherTrack ? eigergletscherTrack?.status : 'CLOSED',
            sismediaResortId: idsSismedia.SISMEDIA_ID_GW,
            sismediaTrackId: idsSismedia.SISMEDIA_TRACK_ID_EIGERGLETSCHER_KLEINE_SCHEIDEGG,
        });

    },

    /**
     *
     */
    modifyTracks() {

        // Adjust Abfahrt finel to have custom icon
        const finel = this.data.tracks.find(item => item.id === 'muerren_schilthorn_track_77_abfahrtsroute_finel');
        if (finel) {
            finel.customTrackIcon = 'ski_tour';
        }

        // Adjust skitour läger to be a piste_variant instead of tracktype "FUN"
        const skiTourLaeger = this.data.tracks.find(item => item.sismediaTrackId === '319');
        if (skiTourLaeger) {
            skiTourLaeger.trackType = 'PISTE_VARIANT';
            skiTourLaeger.difficulty = 'VARIANT';
            skiTourLaeger.customTrackIcon = 'uphill';
        }

        // Adjust bodmi
        const bodmi = this.data.tracks.find(item => item.sismediaTrackId === '187' && item.sismediaResortId === '3');
        if (bodmi) {
            bodmi.customTrackIcon = 'skischool';
        }


        // Adjust Inferno to have type piste medium
        const inferno = this.data.tracks.find(item => item.id === 'muerren_schilthorn_track_335_inferno');
        if (inferno) {
            inferno.trackType = 'PISTE_MEDIUM';
            inferno.difficulty = 'MEDIUM';
            inferno.customTrackIcon = 'inferno';
        }

        // Adjust Skicross to have type piste medium
        const skicross = this.data.tracks.find(item => item.id === 'muerren_schilthorn_track_336_skicross_maulerhubel');
        if (skicross) {
            skicross.trackType = 'PISTE_MEDIUM';
            skicross.difficulty = 'MEDIUM';
            skicross.customTrackIcon = 'skicross';
        }

        // Adjust halfpipe
        const halfpipe = this.data.tracks.find(item => item.id === 'grindelwald_first_track_62_halfpipe_oberjoch');
        if (halfpipe) {
            halfpipe.customTrackIcon = 'halfpipe';
        }

        // Adjust halfpipe
        const paendaSnowpark = this.data.tracks.find(item => item.id === 'muerren_schilthorn_track_36_paenda_snowpark');
        if (paendaSnowpark) {
            paendaSnowpark.customTrackIcon = 'snowpark';
        }

        // Adjust First Flyer to have correct icon
        const flyer = this.data.tracks.find(item => item.id === 'grindelwald_first_track_25_first_flieger');

        if (flyer) {
            flyer.customTrackIcon = 'flyer';
            flyer.name = flyer.name.replace('()', '');
        }

        // Adjust First Flyer to have correct icon
        const flyer2 = this.data.tracks.find(item => item.id === 'grindelwald_first_track_25_first_flieger_()');

        if (flyer2) {
            flyer2.customTrackIcon = 'flyer';
            flyer2.name = flyer2.name.replace('()', '');
        }

        // Adjust First Glider to have correct icon
        const glider = this.data.tracks.find(item => item.id === 'grindelwald_first_track_105_first_glider');
        if (glider) {
            glider.customTrackIcon = 'glider';
        }

        // Adjust First Mountain Cart to have correct icon
        const mountainCart = this.data.tracks.find(item => item.id === 'grindelwald_first_track_101_mountain_cart');
        if (mountainCart) {
            mountainCart.customTrackIcon = 'mountain_cart';
        }

        // Adjust First Mountain Cart to have correct icon
        const trotinet = this.data.tracks.find(item => item.id === 'grindelwald_first_track_93_trottibike_(sommer)');
        if (trotinet) {
            trotinet.customTrackIcon = 'trotinet';
        }

        // Adjust Baergelegg Funpark to have correct icon
        const baergelegg = this.data.tracks.find(item => item.id === 'grindelwald_first_track_26_park_baergelegg');
        if (baergelegg) {
            baergelegg.customTrackIcon = 'snowpark';
        }

        // Adjust Oberjoch Funpark to have correct icon
        const oberjoch = this.data.tracks.find(item => item.id === 'grindelwald_first_track_29_park_oberjoch');
        if (oberjoch) {
            oberjoch.customTrackIcon = 'snowpark';
        }

        // Adjust Oberjoch Race Course to have correct icon
        const oberjochRace = this.data.tracks.find(item => item.id === 'grindelwald_first_track_28_rennstrecke_oberjoch');
        if (oberjochRace) {
            oberjochRace.customTrackIcon = 'lauberhorn_live';
        }

        // Adjust Oberjoch Race Course to have correct icon
        const wengernalpWixi = this.data.tracks.find(item => item.id === 'grindelwald_wengen_track_121_37_wengernalp_-_wixi');
        if (wengernalpWixi) {
            wengernalpWixi.customTrackIcon = 'uphill';
            wengernalpWixi.trackType = 'FUN';
        }

        // Adjust Lily Fun Track
        const lily = this.data.tracks.find(item => item.id === 'grindelwald_first_track_17_fun_slope');
        if (lily) {
            lily.customTrackIcon = 'lily';
        }

        // Adjust Lauberhorn Live to have correct icon
        const lauberhornLive = this.data.tracks.find(item => item.id === 'grindelwald_wengen_track_52_lauberhorn-live');
        if (lauberhornLive) {
            lauberhornLive.customTrackIcon = 'lauberhorn_live';
        }

        // Adjust Flieger Summer Item to have a different sismedia id (distinguish winter/sommer)
        const summerFlieger = this.data.tracks.find(item => item.id === 'grindelwald_first_track_25_first_flieger_(sommerbild)');
        if (summerFlieger) {
            summerFlieger.sismediaTrackId = idsSismedia.SISMEDIA_TRACK_ID_FIRST_FLIEGER_SOMMER;
            summerFlieger.customTrackIcon = 'flyer';
        }

        // Adjust Bachalp Summer Hiking Item to have a different sismedia id (distinguish winter/sommer)
        const summerBachalpTrack = this.data.tracks.find(item => item.id === 'grindelwald_first_track_91_first_-_bachalpsee_(sommerwanderweg)');
        if (summerBachalpTrack) {
            summerBachalpTrack.sismediaTrackId = idsSismedia.SISMEDIA_TRACK_ID_FIRST_BACHALPSEE_SOMMER;
        }


        // Adjust eggboden piste (4b) to be a piste_variant
        const eggbodenTrack = this.data.tracks.find(item => item.sismediaTrackId === '190');
        if (eggbodenTrack) {
            eggbodenTrack.trackType = 'PISTE_VARIANT';
            eggbodenTrack.difficulty = 'VARIANT';
        }

        // Adjust First Schilt Skicross to have custom icon
        const skicrossSchilt = this.data.tracks.find(item => item.id === 'grindelwald_first_track_18_skicross-strecke');
        if (skicrossSchilt) {
            skicrossSchilt.customTrackIcon = 'skicross-first';
        }

        // Adjust Schreckfeld ATC
        const schreckfeldAtc = this.data.tracks.find(item => item.id === 'grindelwald_first_track_330_schreckfeld_atc');
        if (schreckfeldAtc) {
            schreckfeldAtc.customTrackIcon = 'atc';
        }

        // Adjust winteregg Funpark to have correct icon
        const winteregg = this.data.tracks.find(item => item.id === 'muerren_schilthorn_track_404671_snowpark_winteregg');
        if (winteregg) {
            winteregg.customTrackIcon = 'snowpark';
        }

        // Adjust maulerhubel Funpark to have correct icon
        const maulerhubel = this.data.tracks.find(item => item.id === 'muerren_schilthorn_track_404670_snowpark_maulerhubel');
        if (maulerhubel) {
            maulerhubel.customTrackIcon = 'snowpark';
        }

        // Adjust engetal Funpark to have correct icon
        const engetal = this.data.tracks.find(item => item.id === 'muerren_schilthorn_track_404669_snowpark_engetal');
        if (engetal) {
            engetal.customTrackIcon = 'snowpark';
        }

    },

    /**
     * Temporarily only show lift status for the lifts in this exclusive pre season list.
     * This means that if "pre season lifts" are defined, only they will show closed status as "red".
     * Other closed lifts will be "gray".
     */
    applyPreSeasonSettings() {
        if (IS_PRE_SEASON) {
            this.data.lifts.forEach((lift) => {
                if (PRE_SEASON_LIFTS.find((preSeasonLift) => {
                    return lift.sismediaLiftId === preSeasonLift.sismediaLiftId
                    && lift.sismediaResortId === preSeasonLift.sismediaResortId;
                })) {
                    lift.isPreSeasonDisabled = false;
                } else {
                    lift.isPreSeasonDisabled = true;
                }
            });
        }
    },

    /**
     *
     */
    applyActivityModeSettings() {
        this.data.lifts.forEach((lift) => {
            if (LIFTS_HIKING.find((activityLift) => {
                return lift.sismediaLiftId === activityLift.sismediaLiftId
                && lift.sismediaResortId === activityLift.sismediaResortId;
            })) {
                lift.isHiking = true;
            } else {
                lift.isHiking = false;
            }

            if (LIFTS_TOBOGGANING.find((activityLift) => {
                return lift.sismediaLiftId === activityLift.sismediaLiftId
                && lift.sismediaResortId === activityLift.sismediaResortId;
            })) {
                lift.isTobogganing = true;
            } else {
                lift.isTobogganing = false;
            }

            if (LIFTS_NOT_SKIING.find((activityLift) => {
                return lift.sismediaLiftId === activityLift.sismediaLiftId
                && lift.sismediaResortId === activityLift.sismediaResortId;
            })) {
                lift.isNotSkiing = true;
            } else {
                lift.isNotSkiing = false;
            }
        });

        this.data.tracks.forEach((track) => {
            if (TRACKS_HIKING.find((activityTrack) => {
                return track.sismediaTrackId === activityTrack.sismediaTrackId
                && track.sismediaResortId === activityTrack.sismediaResortId;
            })) {
                track.isHiking = true;
            } else {
                track.isHiking = false;
            }

            if (TRACKS_TOBOGGANING.find((activityLift) => {
                return track.sismediaTrackId === activityLift.sismediaTrackId
                && track.sismediaResortId === activityLift.sismediaResortId;
            })) {
                track.isTobogganing = true;
            } else {
                track.isTobogganing = false;
            }
        });

        this.data.pois.forEach((poi) => {
            if (POIS_HIKING.find((activityPoi) => {
                return poi.sismediaPoiId === activityPoi.sismediaPoiId
                && poi.sismediaResortId === activityPoi.sismediaResortId;
            })) {
                poi.isHiking = true;
            } else {
                poi.isHiking = false;
            }
            if (POIS_TOBOGGANING.find((activityPoi) => {
                return poi.sismediaPoiId === activityPoi.sismediaPoiId
                && poi.sismediaResortId === activityPoi.sismediaResortId;
            })) {
                poi.isTobogganing = true;
            } else {
                poi.isTobogganing = false;
            }
            if (POIS_NOT_SKIING.find((activityPoi) => {
                return poi.sismediaPoiId === activityPoi.sismediaPoiId
                && poi.sismediaResortId === activityPoi.sismediaResortId;
            })) {
                poi.isNotSkiing = true;
            } else {
                poi.isNotSkiing = false;
            }
        });
    },

};
